/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useState, Fragment, useEffect } from 'react';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { useTranslation } from 'react-i18next';
import { eventPath } from 'utils/navigation';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionItem,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TabList,
  TabPanels,
  TabPanel
} from '@carbon/react';
import AsyncapiBindings from './AsyncapiBindings';
import Markdown from './Markdown';
import { useApi } from '../contexts/ApiHandler';
import generator from '../utils/generator';

function AsyncApiOperationOverview({ channelName }) {
  // init hooks
  const { apiHandler, api } = useApi();
  const { t } = useTranslation();

  // state
  const [id] = useState(() => uniqueId('channel-overview-'));
  const [parameters, updateParameterValues] = useState([]);

  // vars
  const channelData = apiHandler.getChannel(channelName);
  const markupType = apiHandler.getMarkupType();
  const operations = apiHandler.getChannel(channelName)?.operations().all();
  const description = apiHandler.getDescription(channelName);
  const channelBindings = apiHandler.getChannelBindings(channelName);

  useEffect(() => {
    const parameters = apiHandler.getParameters(channelName);
    parameters.forEach(async (param, index) => {
      await generator
        .generateDummyValue({
          propertyName: 'content',
          property: param.schema || {},
          empty: false
        })
        .then(
          (data) => {
            parameters[index].value = data;
            updateParameterValues(parameters);
          },
          (error) => {
            console.error(error);
          }
        );
    });
  }, [apiHandler, channelName]);

  const renderParameterSchema = () => {
    const parameterType = 'path';
    if (parameters.length === 0) return null;

    return (
      <div className="operationInformation operationSummary operationParms channel__parameter_body">
        <h3 className="channel__labelText">
          {t('parameters')}
        </h3>
        <div className="channelParameters">
          <Accordion align="start">
            <AccordionItem className={classNames(`parameter-${parameterType}`)} key={parameterType} title={t(parameterType)} open>
              <Table>
                <TableBody>
                  {parameters.map((param, index) => {
                    const exampleContent = param.schema && param.schema.type ? param.value : null;
                    return (
                      <Fragment key={`fragment_${param + index}`}>
                        <TableRow>
                          <TableCell className="channel__parameterNameContainer">
                            <div className="channel__parameterName" data-testid="channel-parameter-name">
                              {param.name}
                            </div>
                            <div className="requiredHighlight parameterIn">{t('required')}</div>
                          </TableCell>
                          <TableCell className="channel__parameterDescriptionContainer">
                            {param.description && (
                              <Markdown
                                className="parameterDescription"
                                data={param.description}
                                markupType="markdown"
                                data-testid="channel-description-name"
                              />
                            )}
                            {param.schema && param.schema.type && (
                              <>
                                <div className="parameterType schema">
                                  <div data-testid="channel-schema-type">{param.schema.type}</div>
                                </div>
                                <div className="channel__tabsExample">
                                  <Tabs className="channel__tab">
                                    <TabList aria-label="channel tab">
                                      <Tab title={t('example')} data-testid="channel-example-tab">
                                        {t('example')}
                                      </Tab>
                                    </TabList>
                                    <TabPanels>
                                      <TabPanel>
                                        <div className="cds--snippet--multi channel__tabExample" data-testid="channel-example-snippet">
                                          {exampleContent}
                                        </div>
                                      </TabPanel>
                                    </TabPanels>
                                  </Tabs>
                                </div>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    );
  };

  const channelDescription = (description) => <Markdown data-testid="channel-overview-description" data={description} markupType={markupType} />;

  const renderOperation = (operationLabel, { summary, description }) => {
    const labelId = `${id}-${operationLabel}-operation`.replace(/ /g, '');
    return (
      (summary || description) && (
        <div className="channel__overviewInformation" data-testid="channel-overview-summary">
          <h3 className="channel__label" id={labelId}>
            {operationLabel}
          </h3>
          <section className="channel__value" data-testid="channel-overview-summary-value" aria-labelledby={labelId}>
            {summary ? <p>{summary}</p> : null}
            {description ? <Markdown data={description} markupType={markupType} /> : null}
          </section>
        </div>
      )
    );
  };

  const tableOfContents = () => (
    <div data-testid="channel-tableofcontents" className="channel__value">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>{t('operation')}</TableHeader>
            <TableHeader>{t('description')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {operations.map((operation) => {
            const channelType = operation.action();
            const dataRoute = eventPath(api, channelName, `operation/${operation.id() ? encodeURIComponent(operation.id()) : channelType}`);
            return (
              <TableRow key={operation.id()}>
                <TableCell>
                  <Link to={dataRoute}>
                    <p data-testid={`channel-${channelType}-link`}>{t(`channel_${channelType}_operation`, { channelName: operation.id() })}</p>
                  </Link>
                </TableCell>
                <TableCell>
                  <p>{t(`channel_${channelType}_description`)}</p>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
  let renderTable = true;
  return (
    <section className={classNames('channel', 'main')}>
      <h2 className="channel__header" aria-label={t('channel_overview_label')} data-testid="channel-overview-header">
        {channelName}
      </h2>
      {channelBindings && <AsyncapiBindings bindings={channelBindings} />}
      <div className="channel__body divided">
        {!channelData || Object.values(channelData).length === 0 ? (
          <div className="channel__overviewInformation" data-testid="channel-error" aria-label={t('channel_overview_error_label')}>
            {t('channel_overview_error')}
          </div>
        ) : (
          <>
            {description ? channelDescription(description) : null}
            {renderParameterSchema()}
            {operations.map((operation) => {
              const operationDesc = operation.description();
              const operationSummary = operation.summary();
              if (operationDesc || operationSummary) {
                renderTable = false;
              }
              return (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {operationDesc || operationSummary
                    ? renderOperation(t(`channel_overview_${operation.action()}`, operation.id()), { summary: operationSummary, description: operationDesc })
                    : null}
                </>
              );
            })}
            {renderTable && operations && operations.length > 0 && tableOfContents()}
          </>
        )}
      </div>
    </section>
  );
}

export default AsyncApiOperationOverview;
